import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useGetJobErrorScreenshotQuery } from "../../features/adminUsers/adminUsers";
import { useFilePreview } from "../preview/useFilePreview";
import { HighlightedText } from "../preview/searchUtils";
import { LogRecordDto } from "./logRecords";

// Older than this, a link is asked for again before the full-size view: it
// expires after five minutes.
const LINK_FRESH_MS = 240_000;

function refusalText(error: unknown, fallback: string): string {
  const body = (error as { data?: unknown } | undefined)?.data;
  const message =
    body && typeof body === "object"
      ? (body as Record<string, unknown>).message
      : null;
  return typeof message === "string" && message ? message : fallback;
}

function ErrorScreenshot({ jobId, name }: { jobId: number; name: string }) {
  const { openInlinePreview } = useFilePreview();
  const { data, error, isFetching, refetch, fulfilledTimeStamp } =
    useGetJobErrorScreenshotQuery({ jobId, name });
  const [retried, setRetried] = useState(false);
  const [broken, setBroken] = useState(false);
  const [opening, setOpening] = useState(false);
  const fileName = name.split("/").pop() || "screenshot.png";

  const openFullSize = async () => {
    if (!data || opening) return;
    setOpening(true);
    let url = data.url;
    try {
      if (
        !fulfilledTimeStamp ||
        Date.now() - fulfilledTimeStamp > LINK_FRESH_MS
      ) {
        url = (await refetch().unwrap()).url;
      }
      // Not from the cache: the thumbnail's response carries no CORS headers.
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      openInlinePreview(fileName, await response.blob());
    } catch {
      window.open(url, "_blank", "noopener");
    } finally {
      setOpening(false);
    }
  };

  if (!data && isFetching) return <CircularProgress size={16} />;
  if (error || broken || !data) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {error
          ? refusalText(error, "The screenshot could not be loaded.")
          : "The screenshot could not be loaded."}
      </Typography>
    );
  }
  return (
    <Tooltip title="Open the screenshot">
      <Box
        component="img"
        src={data.url}
        alt="The page when the employee failed"
        loading="lazy"
        onClick={openFullSize}
        onError={() => {
          // An expired link, most likely: one fresh one, then give up.
          if (retried) {
            setBroken(true);
            return;
          }
          setRetried(true);
          void refetch();
        }}
        sx={{
          display: "block",
          width: 220,
          maxWidth: "100%",
          height: 124,
          objectFit: "cover",
          objectPosition: "top left",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          cursor: opening ? "progress" : "zoom-in",
          bgcolor: "background.paper",
        }}
      />
    </Tooltip>
  );
}

/**
 * Why an opened failing employee row failed: the bot's reason, the files of
 * theirs still failing, and the screenshot of the moment. The screenshot link
 * is asked for here, when the row opens - not with the list.
 */
export default function LogFailureDetails({
  record,
  jobId,
  query,
}: {
  record: LogRecordDto;
  jobId: number | null;
  query: string;
}) {
  const files = record.failed_files ?? [];
  const more = Math.max(0, (record.failed_files_total ?? 0) - files.length);
  const screenshot =
    record.screenshot && jobId != null ? record.screenshot : null;
  if (!record.reason && files.length === 0 && !screenshot) return null;
  // A completed employee carries a note, not a failure.
  const succeeded = record.state === "success";

  return (
    <Stack spacing={0.75} sx={{ mt: 0.75, ml: 4, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        {succeeded ? "Note" : "Why it failed"}
      </Typography>
      {record.reason ? (
        <Typography
          variant="body2"
          sx={{
            color: succeeded ? "text.primary" : "error.main",
            wordBreak: "break-word",
          }}
        >
          <HighlightedText text={record.reason} query={query} />
        </Typography>
      ) : null}
      {files.length ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {files.map((file, index) => (
            <Typography
              key={`${file.filename ?? ""}|${index}`}
              component="li"
              variant="body2"
              sx={{ wordBreak: "break-word" }}
            >
              <HighlightedText
                text={[
                  file.filename || "(no file name)",
                  file.reason || "no reason recorded",
                ].join(" - ")}
                query={query}
              />
            </Typography>
          ))}
          {more ? (
            <Typography
              component="li"
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              {`and ${more} more - see File logs`}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {screenshot && jobId != null ? (
        <ErrorScreenshot jobId={jobId} name={screenshot} />
      ) : null}
    </Stack>
  );
}
